/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  Paper,
  Box,
  InputLabel,
  Select,
  FormControl,
  Card,
  Avatar,
  IconButton,
  FormHelperText,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  FirstPage,
  ChevronLeft,
  ChevronRight,
  LastPage,
  Edit,
  Clear,
  Event,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { useQuery } from "@tanstack/react-query";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import handleExport from "utils/exportData";
import { IconTableExport } from "@tabler/icons-react";
import moment from "moment";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { convertUtcToIST } from "utils/ISTConvert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MDBox from "components/MDBox";
import MDAutocomplete from "components/MDAutocomplete";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDTextField from "components/MDTextField";
import LeaveActionDialog from "../leave_management/LeaveActionDialog";
import PropTypes from "prop-types";

const LeaveList = () => {
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    user_name: "",
    district_name: "",
    leave_type: "",
    start_date: null,
    end_date: null,
    reason: "",
    status: "",
  });
  const genders = ["Male", "Female", "Other"];
  const leaveOptions = [
    { title: "On Duty", value: "On Duty" },
    { title: "Sick Leave", value: "Sick Leave" },
    { title: "Casual Leave", value: "Casual Leave" },
    // { title: "Paid Leave", value: "Paid Leave" },
  ];
  const statusOptions = [
    { title: "Approved", value: "Approved" },
    { title: "Pending", value: "Pending" },
    { title: "Declined", value: "Declined" },
  ];
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [exportData, setExportData] = useState([]);
  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const fetchUsers = async (
    pageIndex = 0,
    pageSize = 10,
    filterValues = filters,
    sortValues = sorting,
    columnFilters = []
  ) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pageIndex + 1,
        limit: pageSize,
        columnFilters: JSON.stringify(columnFilters),
        user_name: filterValues.user_name,
        district_name: filterValues.district_name,
        leave_type: filterValues.leave_type,
        start_date: filterValues.start_date
          ? dayjs(filterValues.start_date).format("DD-MM-YYYY")
          : "",
        end_date: filterValues.end_date ? dayjs(filterValues.end_date).format("DD-MM-YYYY") : "",
        reason: filterValues.reason,
        status: filterValues.status,
      });

      // Append sorting info
      if (sortValues.length > 0) {
        const sortField = sortValues[0].id;
        const sortOrder = sortValues[0].desc ? "desc" : "asc";
        queryParams.append("sortField", sortField);
        queryParams.append("sortOrder", sortOrder);
      }

      const authResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/me`,
        {},
        { withCredentials: true }
      );

      const { role_id, user_id } = authResponse.data;
      setUserRole(role_id);

      // Add them to query params
      queryParams.append("role_id", role_id);
      queryParams.append("user_id", user_id);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/leave-request?${queryParams.toString()}`,
        {
          withCredentials: true,
        }
      );

      if (res.data.status === 200) {
        setData(res.data.data);
        setRowCount(res.data.total ?? 0);
        setExportData(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers(pagination.pageIndex, pagination.pageSize, filters, sorting, columnFilters);
  }, [pagination, sorting, columnFilters]);

  useEffect(() => {
    if (id) {
      setSelectedId(id);
      setDialogOpen(true);
      fetchUsers(pagination.pageIndex, pagination.pageSize, filters, sorting, columnFilters);
    }
  }, [id]);

  // Handle filter from navigation state (Notification click or Dashboard)
  useEffect(() => {
    if (location.state?.filterId) {
      setColumnFilters([{ id: "id", value: String(location.state.filterId) }]);
      // Clear state to avoid re-applying on every render or back navigation
      window.history.replaceState({}, document.title);
    } else if (location.state?.status) {
      const statusValue = location.state.status;
      setFilters((prev) => ({ ...prev, status: statusValue }));
      fetchUsers(0, pagination.pageSize, { ...filters, status: statusValue }, sorting, columnFilters);
      // Clear state to avoid re-applying on every render or back navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleFirstPage = () => {
    if (pagination.page > 0) fetchUsers(0);
  };

  const handlePrevPage = () => {
    if (pagination.page > 0) fetchUsers(pagination.page - 1);
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages - 1) fetchUsers(pagination.page + 1);
  };

  const handleLastPage = () => {
    if (pagination.page < pagination.totalPages - 1) fetchUsers(pagination.totalPages - 1);
  };

  const fetchDistrict = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/districts/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: districtData,
    error: districtError,
    isLoading: districtIsLoading,
  } = useQuery({
    queryKey: ["fetchDistrict"],
    queryFn: fetchDistrict,
    retry: false,
  });

  const fetchStatus = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/status/all`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: fetchedStatus,
    error: statusError,
    isLoading: statusIsLoading,
  } = useQuery({
    queryKey: ["fetchStatus"],
    queryFn: fetchStatus,
    retry: false,
  });

  const statusData =
    fetchedStatus?.map((status) => ({
      title: status.name,
      value: status.status_id,
    })) || [];

  const fetchAllUsers = async () => {
    const response = await axios({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/user/all?page=1&limit=10000`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data.data;
  };

  const {
    data: usersData,
    error: usersError,
    isLoading: usersIsLoading,
  } = useQuery({
    queryKey: ["fetchAllUsers"],
    queryFn: fetchAllUsers,
    retry: false,
  });

  const allUsersData = useMemo(() => {
    if (!usersData) return [];
    const uniqueNames = [...new Set(usersData.map((user) => user.user_name).filter(Boolean))];
    return uniqueNames.map((name) => ({
      title: name,
      value: name,
    }));
  }, [usersData]);

  const districtOptions = useMemo(() => {
    return (
      districtData?.map((item) => ({
        label: item.district_name || item.name || item.title || "",
        value: item.district_name || item.name || item.title || "",
      })) || []
    );
  }, [districtData]);

  // Reusable DatePicker Filter Component
  // eslint-disable-next-line react/prop-types
  const DatePickerFilter = ({ column }) => {
    const [open, setOpen] = useState(false);
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          format="DD-MM-YYYY"
          value={column.getFilterValue() ? dayjs(column.getFilterValue(), "DD-MM-YYYY") : null}
          onChange={(newValue) => {
            column.setFilterValue(newValue ? newValue.format("DD-MM-YYYY") : undefined);
          }}
          slotProps={{
            textField: {
              onClick: () => setOpen(true),
              placeholder: "Filter Date",
              variant: "standard",
              fullWidth: true,
              InputProps: {
                endAdornment: (
                  <React.Fragment>
                    {column.getFilterValue() && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          column.setFilterValue(undefined);
                        }}
                        size="small"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => setOpen(true)}>
                      <Event fontSize="small" />
                    </IconButton>
                  </React.Fragment>
                ),
              },
            },
            actionBar: {
              actions: ["cancel", "accept"],
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const columns = useMemo(() => {
    const baseColumns = [
      ...(userRole !== 3
        ? [
          {
            accessorKey: "user_name",
            header: "Green Fellow",
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
            Filter: ({ column }) => {
              return (
                <Autocomplete
                  options={allUsersData}
                  getOptionLabel={(option) => option.title || ""}
                  value={
                    allUsersData.find((option) => option.value === column.getFilterValue()) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    column.setFilterValue(newValue ? newValue.value : undefined);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Filter User"
                      variant="standard"
                      sx={{ minWidth: "120px" }}
                    />
                  )}
                />
              );
            },
          },
          {
            accessorKey: "district_name",
            header: "District",
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
            Filter: ({ column }) => {
              return (
                <Autocomplete
                  options={districtOptions}
                  value={
                    districtOptions.find((option) => option.value === column.getFilterValue()) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    column.setFilterValue(newValue ? newValue.value : undefined);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Filter District"
                      variant="standard"
                      sx={{ minWidth: "120px" }}
                    />
                  )}
                />
              );
            },
          },
        ]
        : []),
      {
        accessorKey: "leave_type",
        header: "Leave Type",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Filter: ({ column }) => {
          return (
            <Autocomplete
              options={leaveOptions}
              getOptionLabel={(option) => option.title || ""}
              value={
                leaveOptions.find((option) => option.value === column.getFilterValue()) || null
              }
              onChange={(event, newValue) => {
                column.setFilterValue(newValue ? newValue.value : undefined);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter Type"
                  variant="standard"
                  sx={{ minWidth: "120px" }}
                />
              )}
            />
          );
        },
      },
      {
        accessorKey: "start_date",
        header: "Start Date",
        size: 150,
        Filter: DatePickerFilter,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          return convertUtcToIST(row?.original?.start_date) ?? "-";
        },
      },
      {
        accessorKey: "end_date",
        header: "End Date",
        size: 150,
        Filter: DatePickerFilter,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          return convertUtcToIST(row?.original?.end_date) ?? "-";
        },
      },
      {
        accessorKey: "reason",
        header: "Reason",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => {
          const reason = cell.getValue() || "";
          const isTruncated = reason.length > 20;
          const displayText = isTruncated ? reason.substring(0, 20) + "..." : reason;

          return isTruncated ? (
            <Tooltip title={reason} arrow>
              <span>{displayText}</span>
            </Tooltip>
          ) : (
            <span>{displayText}</span>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Filter: ({ column }) => {
          return (
            <Autocomplete
              options={statusOptions}
              getOptionLabel={(option) => option.title || ""}
              value={
                statusOptions.find((option) => option.value === column.getFilterValue()) || null
              }
              onChange={(event, newValue) => {
                column.setFilterValue(newValue ? newValue.value : undefined);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter Status"
                  variant="standard"
                  sx={{ minWidth: "120px" }}
                />
              )}
            />
          );
        },
        Cell: ({ row }) => {
          return (
            <Chip
              sx={{
                height: "auto",
                "& .MuiChip-label": {
                  color: "white !important",
                  fontSize: "14px",
                  fontWeight: "bold",
                },
              }}
              color={
                row?.original?.status === "Approved"
                  ? "success"
                  : row?.original?.status === "Declined"
                    ? "error"
                    : "warning"
              }
              label={row?.original?.status}
            />
          ); // ?? row?.original?.status ?? "-";
        },
      },
      {
        accessorKey: "decline_reason",
        header: "Reason for Declining",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => row.original.decline_reason || "-",
        size: 300,
      },
    ];
    if (userRole !== 3) {
      baseColumns.push({
        accessorKey: "edit",
        header: "Action",
        enableColumnFilter: false,
        // size: 100,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          return (
            // Only show Edit button if row is selected
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Tooltip title={"Click to Approve/Decline"}>
                <span>
                  {" "}
                  {/* Needed to wrap disabled buttons with tooltips */}
                  <IconButton
                    key={row.id}
                    onClick={() => {
                      const currentStatus = row.original.status;
                      if (currentStatus === "Approved" || currentStatus === "Declined") {
                        setMessage({
                          status: "error",
                          title: "Action Not Allowed",
                          msg: "Only Pending leave requests can be Approved or Declined.",
                        });
                        return;
                      }
                      window.history.pushState({}, "", `/leave-list/${row.original.id}`);
                      setSelectedId(row.original.id); // Only pass ID
                      setDialogOpen(true);
                    }}
                    disabled={loading}
                    sx={{
                      color: "#f48c06",
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                      transition: "background-color 0.3s, transform 0.3s",
                      "&:hover": {
                        backgroundColor: "#f48c06",
                        color: "#fff",
                        transform: "scale(1.05)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Edit sx={{ width: 20, height: 20 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ); // Hide button if not selected
        },
        // muiTableHeadCellProps: {
        //   sx: {
        //     color: "#fff",
        //     backgroundColor: "primary.main", // Match your table header background
        //     zIndex: 3,
        //   },
        // },

        enableSorting: false,
      });
    }
    return baseColumns;
  }, [userRole, loading]);

  const handleExportData = async () => {
    try {
      let formattedData = [];

      // If no rows are selected, check if there's any data at all to export
      if (!(rowSelection && Object.keys(rowSelection).length > 0)) {
        if (rowCount === 0) {
          setMessage({
            status: "warning",
            title: "Warning",
            msg: "No data found to export.",
          });
          return;
        }
      }

      if (rowSelection && Object.keys(rowSelection).length > 0) {
        formattedData = exportData.filter((item) => rowSelection[item.id]);
      } else {
        // Fetch current user details to ensure proper filtering on the backend
        const authResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/auth/me`,
          {},
          { withCredentials: true }
        );

        const { role_id, user_id } = authResponse.data;

        const queryParams = new URLSearchParams({
          user_name: filters.user_name,
          district_name: filters.district_name,
          leave_type: filters.leave_type,
          start_date: filters.start_date ? dayjs(filters.start_date).format("DD-MM-YYYY") : "",
          end_date: filters.end_date ? dayjs(filters.end_date).format("DD-MM-YYYY") : "",
          reason: filters.reason,
          status: filters.status,
          page: 1,
          limit: 10000,
          role_id: role_id,
          user_id: user_id,
        });

        if (sorting.length > 0) {
          const sortField = sorting[0].id;
          const sortOrder = sorting[0].desc ? "desc" : "asc";
          queryParams.append("sortField", sortField);
          queryParams.append("sortOrder", sortOrder);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/leave-request?${queryParams.toString()}`,
          { withCredentials: true }
        );

        if (res.data.status === 200) {
          formattedData = res.data.data;
        }
      }

      if (formattedData.length === 0) {
        // console.log("No data found to export");
        setMessage({
          status: "error",
          title: "Error",
          msg: "No data found to export.",
        });
        return;
      }

      // console.log("formattedData", formattedData);
      await handleExport(formattedData, "Leave_List_Data", districtData, statusData);

      setMessage({
        status: "success",
        title: "Success",
        msg: "Excel downloaded successfully!",
      });
    } catch (error) {
      console.error(" Export error:", error);
      setMessage({
        status: "error",
        title: "Error",
        msg: "Something went wrong during export.",
      });
    }
  };

  const handleActionSuccess = () => {
    fetchUsers(); // refetch the list manually
  };

  return (
    <DashboardLayout>
      {/* <Card id="delete-account">
        
        <MDBox mb={2} />
      </Card> */}
      <MDBox mt={3} />
      <Card id="delete-account" sx={{ mb: 4 }}>
        <MDBox pt={2} px={2}>
          {/* <MDBox mt={5} mb={3}> */}
          <Grid container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <MDTypography variant="h5" fontWeight="medium">
                Leave List
              </MDTypography>
            </Grid>
            {userRole === 3 && (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} display="flex" justifyContent="flex-end">
                <MDButton
                  color={"dark"}
                  onClick={() => {
                    navigate("/leave-request/add");
                  }}
                  sx={{
                    color: "white !important",
                    backgroundColor: "#1976d2",
                    "&:hover": {
                      backgroundColor: "#155fa0",
                    },
                    minWidth: "120px",
                  }}
                >
                  + Add Leave Request
                </MDButton>
              </Grid>
            )}
          </Grid>
          {/* </MDBox> */}
        </MDBox>

        <MDBox p={2}>
          <Grid container spacing={3}>
            {(userRole === 1 || userRole === 2) && (
              <>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="User Name"
                    name="user_name"
                    value={filters.user_name}
                    options={allUsersData}
                    onChange={(newValue) => {
                      setFilters({ ...filters, user_name: newValue });
                    }}
                    isFilter={true}
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="District"
                    name="district_name"
                    value={filters.district_name}
                    options={districtData}
                    onChange={(newValue) => {
                      setFilters({ ...filters, district_name: newValue });
                    }}
                    isFilter={true} // optional: add a flag if you need special styling
                  />
                </Grid>
              </>
            )}

            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="Leave Type"
                name="leave_type"
                value={filters.leave_type}
                options={leaveOptions}
                onChange={(newValue) => {
                  setFilters({ ...filters, leave_type: newValue });
                }}
                isFilter={true} // optional: add a flag if you need special styling
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 4 }}>
              <MDTypography style={{ fontSize: "16px" }} color="dark">
                Start Date
              </MDTypography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={filters.start_date}
                  format="DD/MM/YYYY"
                  onChange={(value) => setFilters({ ...filters, start_date: value })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      InputProps: {
                        readOnly: true,
                        sx: {
                          fontSize: "14px",
                          height: "45px",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDTypography style={{ fontSize: "16px" }} color="dark">
                End Date
              </MDTypography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={filters.end_date}
                  format="DD/MM/YYYY"
                  onChange={(value) => setFilters({ ...filters, end_date: value })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      InputProps: {
                        readOnly: true,
                        sx: {
                          fontSize: "14px",
                          height: "45px",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDTextField
                name="reason"
                value={filters.reason}
                onChange={(e) => setFilters({ ...filters, reason: e })}
                placeholder="Enter Reason"
                displayName="Reason"
                inputType="onlyString"
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDAutocomplete
                label="Status"
                name="status"
                value={filters.status}
                options={statusOptions}
                onChange={(newValue) => {
                  setFilters({ ...filters, status: newValue });
                }}
                isFilter={true} // optional: add a flag if you need special styling
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="flex-end">
            {/* Search Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                onClick={() => fetchUsers(0, pagination.pageSize, filters, sorting)}
                sx={{
                  color: "white !important",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#155fa0",
                  },
                  minWidth: "120px",
                }}
              >
                Search
              </MDButton>
            </Grid>

            {/* Cancel Button */}
            <Grid item xs={12} md={2} textAlign="center">
              <MDButton
                variant="outlined"
                onClick={() => {
                  const cleared = {
                    user_name: "",
                    district_name: "",
                    leave_type: "",
                    start_date: null,
                    end_date: null,
                    reason: "",
                    status: "",
                  };
                  setFilters(cleared);
                  fetchUsers(0, pagination.pageSize, cleared);
                }}
                sx={{
                  color: "#b71c1c !important",
                  borderColor: "#b71c1c",
                  "&:hover": {
                    backgroundColor: "#fbe9e7",
                    borderColor: "#b71c1c",
                  },
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <Box>
        <Grid container spacing={5}>
          <Grid size={{ xs: 12, md: 12 }} className="listTable">
            {/* <Card sx={{ padding: 3, boxShadow: 3 }}> */}
            {/* {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : ( */}
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Paper
                elevation={2}
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  p: 2,
                }}
              >
                <MaterialReactTable
                  columns={columns}
                  data={data}
                  getRowId={(row) => row.id}
                  enableGlobalFilter={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={false}
                  initialState={{ showColumnFilters: false }}
                  displayColumnDefOptions={{
                    "mrt-row-numbers": {
                      size: 40,
                      maxSize: 40,
                      position: "left",
                      muiTableHeadCellProps: { align: "center" },
                      muiTableBodyCellProps: { align: "center" },
                    },
                  }}
                  layoutMode="grid"
                  enableRowNumbers
                  manualFiltering
                  manualPagination
                  manualSorting
                  onPaginationChange={setPagination}
                  onSortingChange={setSorting}
                  rowCount={rowCount}
                  // onColumnFiltersChange={setColumnFilters}
                  onColumnFiltersChange={(filters) => {
                    setColumnFilters(filters);
                    fetchUsers(
                      pagination.pageIndex,
                      pagination.pageSize,
                      filters,
                      sorting,
                      filters
                    );
                  }}
                  // onGlobalFilterChange={setGlobalFilter}
                  // onPaginationChange={setPagination}
                  // onSortingChange={setSorting}
                  // rowCount={rowCount}
                  state={{
                    columnFilters,
                    // globalFilter,
                    // isLoading,
                    loading,
                    pagination,
                    // showAlertBanner: isError,
                    // showProgressBars: isRefetching,
                    sorting,
                  }}
                  muiTableHeadProps={{
                    sx: {
                      padding: "0px", // won't apply directly to <thead>, but useful for general structure
                      backgroundColor: "#011F3A", // background on <thead> element itself
                    },
                  }}
                  enableStickyHeader
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "70vh",
                      overflowY: "auto",
                    },
                  }}
                  enableColumnActions={false}
                  muiTableHeadCellProps={{
                    sx: {
                      backgroundColor: "#004030",
                      color: "#fff",
                      // fontSize: '16px',
                      fontWeight: "bold",
                      position: "sticky", // Make header sticky
                      top: 0, // Stick it at the top
                      zIndex: 1, // Ensure it stays
                      "& .MuiTableSortLabel-root": {
                        "& .MuiTableSortLabel-icon": {
                          color: "#fff !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                        fontSize: "12px",
                        "::placeholder": {
                          color: "#e0e0e0", // Default (blurred) placeholder color
                          opacity: 1,
                        },
                      },
                      "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                        color: "#ffffff", // Focused placeholder color
                      },
                      "& .MuiIconButton-root": {
                        color: "#fff",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#e0e0e0",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff",
                      },

                      "& .MuiCheckbox-root": {
                        color: "#fff",
                      },
                    },
                  }}
                  muiTableBodyCellProps={{
                    sx: {
                      padding: "8px", // reduce from default 16px
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  muiTableBodyRowProps={({ row }) => ({
                    sx: {
                      backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                    },
                  })}
                  renderTopToolbarCustomActions={() => (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        flexWrap: "wrap",
                        marginLeft: "auto",
                      }}
                    >
                      <Tooltip title={"Export data to excel sheet"} arrow>
                        <Button
                          onClick={handleExportData}
                          sx={{
                            background:
                              "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                            color: "#f4f4f4",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            transition: "background 0.3s, transform 0.3s",
                            "&:hover": {
                              background:
                                "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                              transform: "scale(1.05)",
                            },
                            "&:active": {
                              transform: "scale(0.95)",
                            },
                          }}
                          startIcon={<IconTableExport />}
                        >
                          {"Export To Excel"}
                        </Button>
                      </Tooltip>
                      {/* <Button
                        disabled={table.getPrePaginationRowModel().rows.length === 0}
                        //export all rows, including from the next page, (still respects filtering and sorting)
                        onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
                        startIcon={<FileDownloadIcon />}
                      >
                        Export All Rows
                      </Button> */}
                      {/* <Button
                        disabled={table.getRowModel().rows.length === 0}
                        //export all rows as seen on the screen (respects pagination, sorting, filtering, etc.)
                        onClick={() => handleExportRows(table.getRowModel().rows)}
                        startIcon={<FileDownloadIcon />}
                      >
                        Export Page Rows
                      </Button> */}
                      {/* <Button
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        //only export selected rows
                        onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                        startIcon={<FileDownloadIcon />}
                      >
                        Export Selected Rows
                      </Button>*/}
                    </Box>
                  )}
                  muiTopToolbarProps={{
                    sx: {
                      backgroundColor: "#fff",
                      boxShadow: "none",
                    },
                  }}
                />
              </Paper>
            </Box>
            {/* )} */}
            {/* </Card> */}
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => setMessage({ status: "", title: "", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setMessage({ status: "", title: "", msg: "" })}
          severity={message.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message.msg}
        </Alert>
      </Snackbar>
      <Footer />
      <LeaveActionDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          navigate("/leave-list", { replace: true });
        }}
        leaveId={selectedId}
        onSuccess={handleActionSuccess}
        heading={"Leave Request Details"}
        headerBackgroundColors={["#004030", "#191919"]}
      />
    </DashboardLayout>
  );
};


export default LeaveList;
